import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import * as feedbackApi from '@/api/feedback.api';
import type { FeedbackType, FeedbackRating, SourceType } from '@/api/feedback.api';

/**
 * Query keys for feedback
 */
export const feedbackKeys = {
  all: ['feedback'] as const,
  list: (params?: Record<string, unknown>) => [...feedbackKeys.all, 'list', params] as const,
  stats: (days: number) => [...feedbackKeys.all, 'stats', days] as const,
  providers: () => [...feedbackKeys.all, 'providers'] as const,
  trends: (days: number) => [...feedbackKeys.all, 'trends', days] as const,
  issues: () => [...feedbackKeys.all, 'issues'] as const,
  source: (sourceId: string, sourceType: string) =>
    [...feedbackKeys.all, 'source', sourceId, sourceType] as const,
};

/**
 * Fetch feedback list
 */
export function useFeedback(params?: {
  type?: FeedbackType;
  rating?: FeedbackRating;
  aiProvider?: string;
  userId?: string;
  days?: number;
  limit?: number;
  offset?: number;
}) {
  return useQuery({
    queryKey: feedbackKeys.list(params),
    queryFn: () => feedbackApi.getFeedback(params),
    staleTime: 5 * 60 * 1000,
  });
}

/**
 * Fetch feedback stats
 */
export function useFeedbackStats(days = 30) {
  return useQuery({
    queryKey: feedbackKeys.stats(days),
    queryFn: () => feedbackApi.getFeedbackStats(days),
    staleTime: 5 * 60 * 1000,
  });
}

/**
 * Fetch provider accuracies
 */
export function useProviderAccuracies() {
  return useQuery({
    queryKey: feedbackKeys.providers(),
    queryFn: feedbackApi.getProviderAccuracies,
    staleTime: 5 * 60 * 1000,
  });
}

/**
 * Fetch feedback trends
 */
export function useFeedbackTrends(days = 30) {
  return useQuery({
    queryKey: feedbackKeys.trends(days),
    queryFn: () => feedbackApi.getFeedbackTrends(days),
    staleTime: 5 * 60 * 1000,
  });
}

/**
 * Fetch issue accuracy
 */
export function useIssueAccuracy() {
  return useQuery({
    queryKey: feedbackKeys.issues(),
    queryFn: feedbackApi.getIssueAccuracy,
    staleTime: 5 * 60 * 1000,
  });
}

/**
 * Fetch feedback for source
 */
export function useFeedbackForSource(sourceId: string, sourceType: SourceType) {
  return useQuery({
    queryKey: feedbackKeys.source(sourceId, sourceType),
    queryFn: () => feedbackApi.getFeedbackForSource(sourceId, sourceType),
    enabled: !!sourceId && !!sourceType,
    staleTime: 5 * 60 * 1000,
  });
}

/**
 * Submit feedback mutation
 */
export function useSubmitFeedback() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: feedbackApi.submitFeedback,
    onSuccess: () => {
      // Invalidate relevant queries
      queryClient.invalidateQueries({ queryKey: feedbackKeys.all });
    },
  });
}
